import React, { useState } from "react";
import Icon from "rc-app/components/Icons";

const ResetPasswordPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/auth/reset-password`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || "Failed to reset password. The link may have expired.");
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#161F28] rounded-2xl border border-[#374B5E] p-8 shadow-2xl text-center">
          <p className="text-red-400 text-sm">Invalid or missing reset link. Please request a new one.</p>
          <a href="/" className="mt-4 inline-block text-[#008564] text-sm hover:underline">← Back to home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Icon name="OpenSkyLogoWithText" className="w-40 h-14" />
        </div>

        <div className="bg-[#161F28] rounded-2xl border border-[#374B5E] p-8 shadow-2xl">
          {success ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-14 h-14 rounded-full bg-[#008564]/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-[#008564]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-white text-xl font-semibold">Password updated!</h1>
              <p className="text-[#889DB2] text-sm text-center">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <a
                href="/"
                className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm text-center transition-colors block"
              >
                Sign In
              </a>
            </div>
          ) : (
            <>
              <h1 className="text-white text-2xl font-semibold mb-1">Set new password</h1>
              <p className="text-[#889DB2] text-sm mb-6">Choose a strong password for your account.</p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">New Password</label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#889DB2] hover:text-white transition-colors text-xs"
                    >
                      {showNew ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[#889DB2] text-sm font-medium">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-[#1C2C3A] border border-[#374B5E] text-white placeholder-[#4A5C6A] text-sm outline-none focus:border-[#008564] focus:ring-1 focus:ring-[#008564] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#889DB2] hover:text-white transition-colors text-xs"
                    >
                      {showConfirm ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-2 w-full py-3 rounded-lg bg-[#008564] hover:bg-[#006b51] text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Updating..." : "Set New Password"}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-[#889DB2] text-xs mt-6">
          © {new Date().getFullYear()} OpenSky. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
